import { TslRegistry } from '../tsl/core/registry';
import { formatFieldAddress, parseFieldAddress } from './address';
/**
 * CZNA-217 — the relations the query line offers, in suggestion order, and the backend
 * relation each one compiles to. Words come from the translations (`obsahuje`, `začíná`,
 * `je`, `odpovídá`, `existuje`).
 */
export var FIELD_QUERY_RELATIONS = [
    { code: 'STR_CONTAINS', relation: 'CONTAINS' },
    { code: 'STR_STARTS', relation: 'STARTS_WITH' },
    { code: 'STR_EQ', relation: 'EQUALS' },
    { code: 'STR_MATCHES', relation: 'MATCHES' },
    { code: 'STR_FILLED', relation: 'EXISTS' },
];
var RELATION_CODES = FIELD_QUERY_RELATIONS.map(function (entry) { return entry.code; });
/** Pseudo-field key of the `Typ je …` condition. */
export var TYPE_PSEUDO_KEY = 'type';
/** Translation key the registry asks for the `Typ` label. */
export var TYPE_LABEL_KEY = 'TYPE';
/** First id handed to an address-shaped field; catalog fields use 1…n below it. */
var DYNAMIC_ID_BASE = 100000;
/**
 * Catalog labels carry parentheses and dashes (`Osobní jméno (záhlaví)`), which the
 * tokenizer splits off — the alias without them is the form a user can type unquoted.
 */
var typeableLabel = function (label) {
    return label.replace(/[()—–]/g, ' ').replace(/\s+/g, ' ').trim();
};
/**
 * Builds the TSL registry of the query line: catalog fields by name, every MARC address
 * (`150a`, `1XX`, `XXX`) as a dynamic field, `Typ je …` as a pseudo field. NOT negates
 * whole conditions and the field must be followed by an operator — see the CZNA-217
 * options in `TslRegistryConfig`.
 */
export var createFieldQueryRegistry = function (_a) {
    var catalog = _a.catalog, recordTypes = _a.recordTypes, translate = _a.translate;
    var labels = new Map();
    var catalogTags = new Set(catalog.fields.map(function (field) { return field.tag; }));
    var fields = catalog.fields
        .filter(function (field) { return field.label; })
        .map(function (field, index) {
        labels.set("field:".concat(field.tag), field.label);
        labels.set("fieldAlias:".concat(field.tag), typeableLabel(field.label));
        return {
            id: index + 1,
            nameKey: "field:".concat(field.tag),
            aliasKeys: ["fieldAlias:".concat(field.tag)],
            dataType: 'STRING',
            key: field.tag,
            availableRelations: RELATION_CODES,
            defaultRelation: 'STR_CONTAINS',
        };
    });
    var nextDynamicId = DYNAMIC_ID_BASE;
    var resolveDynamicField = function (word) {
        var _a;
        var address = parseFieldAddress(word);
        // An unknown numeric tag (`920`) is still a field, so the compiler can mark it as
        // unknown in place; an unknown letter tag is just a word. A mask (`XXX`, `X00`) is
        // digits and X only, so it is never a letter tag even when it starts with X.
        var letterTag = !/^[0-9X]{3}$/.test((_a = address === null || address === void 0 ? void 0 : address.field) !== null && _a !== void 0 ? _a : '');
        if (!address || (letterTag && !catalogTags.has(address.field))) {
            return undefined;
        }
        var key = formatFieldAddress(address);
        labels.set("address:".concat(key), key);
        nextDynamicId += 1;
        return {
            id: nextDynamicId,
            nameKey: "address:".concat(key),
            dataType: 'STRING',
            key: key,
            availableRelations: RELATION_CODES,
            defaultRelation: 'STR_CONTAINS',
        };
    };
    return new TslRegistry({
        translate: function (key) { var _a; return (_a = labels.get(key)) !== null && _a !== void 0 ? _a : translate(key); },
        fields: fields,
        pseudoFields: [{
                key: TYPE_PSEUDO_KEY,
                labelKey: TYPE_LABEL_KEY,
                values: recordTypes.map(function (type) { return ({ id: type.code, label: type.label }); }),
            }],
        resolveDynamicField: resolveDynamicField,
        negation: 'clause',
        checkDisjunction: false,
        fieldNeedsOperator: true,
    });
};
